/* eslint-disable @typescript-eslint/no-explicit-any */
// src/components/EventForm.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDarkMode } from "../context/DarkModeContext";
import { eventService } from "../service/eventService";
import type {
  CreateEventData,
  Event,
  EventType,
  EventAudience,
} from "../types/event";
import { Save, X } from "lucide-react";

interface EventFormProps {
  event?: Event;
  onSave?: (event: Event) => void;
  onCancel?: () => void;
}

const EventForm: React.FC<EventFormProps> = ({ event, onSave, onCancel }) => {
  const navigate = useNavigate();
  const { isDark } = useDarkMode();

  const [formData, setFormData] = useState<CreateEventData>({
    title: "",
    description: "",
    startDate: "",
    endDate: "",
    isAllDay: false,
    type: "workshop" as EventType,
    platform: "",
    meetingLink: "",
    location: "",
    audience: "public" as EventAudience,
    maxAttendees: undefined,
    isRecurring: false,
    recurrenceRule: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to format date for datetime-local input
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return "";

    const date = new Date(dateString);

    // Handle invalid dates
    if (isNaN(date.getTime())) {
      console.warn("Invalid date string:", dateString);
      return "";
    }

    // Format to YYYY-MM-DDTHH:MM (datetime-local input format)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Helper function to format date for API (ensure seconds are included)
  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return "";

    // If it's already a full ISO string, return it
    if (dateString.includes(":")) {
      const date = new Date(dateString);
      return date.toISOString();
    }

    // If it's in datetime-local format (YYYY-MM-DDTHH:MM), add seconds
    if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
      return `${dateString}:00`;
    }

    return dateString;
  };

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title,
        description: event.description || "",
        startDate: formatDateForInput(event.startDate),
        endDate: formatDateForInput(event.endDate),
        isAllDay: event.isAllDay,
        type: event.type,
        platform: event.platform,
        meetingLink: event.meetingLink || "",
        location: event.location || "",
        audience: event.audience,
        maxAttendees: event.maxAttendees || undefined,
        isRecurring: event.isRecurring,
        recurrenceRule: event.recurrenceRule || "",
      });
    }
  }, [event]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Prepare data for API - ensure dates are properly formatted
      const apiData = {
        ...formData,
        startDate: formatDateForAPI(formData.startDate),
        endDate: formatDateForAPI(formData.endDate),
      };

      console.log("Submitting event data:", apiData);

      let savedEvent: Event;

      if (event) {
        savedEvent = await eventService.updateEvent(event.id, apiData);
      } else {
        savedEvent = await eventService.createEvent(apiData);
      }

      if (onSave) {
        onSave(savedEvent);
      } else {
        navigate("/dashboard/events-manager");
      }
    } catch (err: any) {
      const errorMessage = err.message || "Failed to save event";
      setError(errorMessage);
      console.error("Error saving event:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : type === "number"
            ? value === ""
              ? undefined
              : parseInt(value)
            : value,
    }));
  };

  // Validate form before submission
  const isFormValid = () => {
    if (!formData.title.trim()) return false;
    if (!formData.startDate) return false;
    if (!formData.endDate) return false;
    if (!formData.type) return false;
    if (!formData.audience) return false;
    if (!formData.platform.trim()) return false;

    // Validate date order
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    if (start >= end) return false;

    return true;
  };

  return (
    <div
      className={`max-w-4xl mx-auto p-6 rounded-xl ${
        isDark ? "bg-gray-800" : "bg-white"
      }`}
    >
      <div className="flex justify-between items-center mb-6">
        <h2
          className={`text-2xl font-bold ${isDark ? "text-white" : "text-blue-800"}`}
        >
          {event ? "Edit Event" : "Create New Event"}
        </h2>
        {onCancel && (
          <button
            onClick={onCancel}
            className={`p-2 rounded-lg ${
              isDark
                ? "hover:bg-gray-700 text-gray-400"
                : "hover:bg-blue-100 text-blue-600"
            }`}
          >
            <X className="w-6 h-6" />
          </button>
        )}
      </div>

      {error && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            isDark
              ? "bg-red-900/20 border border-red-800"
              : "bg-red-50 border border-red-200"
          }`}
        >
          <p className={isDark ? "text-red-400" : "text-red-600"}>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-blue-700"}`}
            >
              Event Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  : "bg-white border-blue-200 text-gray-900 placeholder-blue-400"
              }`}
              placeholder="Enter event title"
            />
          </div>

          <div className="md:col-span-2">
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-blue-700"}`}
            >
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  : "bg-white border-blue-200 text-gray-900 placeholder-blue-400"
              }`}
              placeholder="Describe the event..."
            />
          </div>

          <div>
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-blue-700"}`}
            >
              Event Type *
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white border-blue-200 text-gray-900"
              }`}
            >
              <option value="workshop">Workshop</option>
              <option value="webinar">Webinar</option>
              <option value="networking">Networking</option>
              <option value="qna">Q&A Session</option>
              <option value="conference">Conference</option>
              <option value="training">Training</option>
              <option value="social">Social Event</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div>
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-blue-700"}`}
            >
              Audience *
            </label>
            <select
              name="audience"
              value={formData.audience}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white border-blue-200 text-gray-900"
              }`}
            >
              <option value="public">Public</option>
              <option value="community">Community</option>
              <option value="premium">Premium</option>
              <option value="admin">Admin</option>
              <option value="internal">Internal</option>
            </select>
          </div>
        </div>

        {/* Date & Time */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-blue-700"}`}
            >
              Start Date & Time *
            </label>
            <input
              type="datetime-local"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white border-blue-200 text-gray-900"
              }`}
            />
          </div>

          <div>
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-blue-700"}`}
            >
              End Date & Time *
            </label>
            <input
              type="datetime-local"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white border-blue-200 text-gray-900"
              }`}
            />
          </div>
        </div>

        {/* Platform & Location */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-blue-700"}`}
            >
              Platform *
            </label>
            <input
              type="text"
              name="platform"
              value={formData.platform}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  : "bg-white border-blue-200 text-gray-900 placeholder-blue-400"
              }`}
              placeholder="e.g., Zoom, LinkedIn Live, Discord"
            />
          </div>

          <div>
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-blue-700"}`}
            >
              Meeting Link
            </label>
            <input
              type="url"
              name="meetingLink"
              value={formData.meetingLink}
              onChange={handleChange}
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  : "bg-white border-blue-200 text-gray-900 placeholder-blue-400"
              }`}
              placeholder="https://..."
            />
          </div>

          <div className="md:col-span-2">
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-blue-700"}`}
            >
              Location
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  : "bg-white border-blue-200 text-gray-900 placeholder-blue-400"
              }`}
              placeholder="Physical location or virtual venue"
            />
          </div>
        </div>

        {/* Capacity */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-blue-700"}`}
            >
              Maximum Attendees
            </label>
            <input
              type="number"
              name="maxAttendees"
              value={formData.maxAttendees || ""}
              onChange={handleChange}
              min="1"
              className={`w-full px-4 py-2 rounded-lg border ${
                isDark
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  : "bg-white border-blue-200 text-gray-900 placeholder-blue-400"
              }`}
              placeholder="Leave empty for unlimited"
            />
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              name="isAllDay"
              checked={formData.isAllDay}
              onChange={handleChange}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-blue-700"}`}
            >
              All-day event
            </label>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex space-x-4 pt-6">
          <button
            type="submit"
            disabled={isLoading || !isFormValid()}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 ${
              isDark
                ? "bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-400"
                : "bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white disabled:opacity-50"
            }`}
          >
            <Save className="w-4 h-4" />
            <span>
              {isLoading
                ? "Saving..."
                : event
                  ? "Update Event"
                  : "Create Event"}
            </span>
          </button>

          <button
            type="button"
            onClick={onCancel || (() => navigate("/dashboard/create-event"))}
            className={`px-6 py-3 rounded-lg border ${
              isDark
                ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                : "border-blue-300 text-blue-600 hover:bg-blue-50"
            }`}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EventForm;
